<script setup>
import { computed, toRef } from 'vue';
import { useDialogA11y } from 'dashboard/composables/useDialogA11y';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: '' },
  disableEscapeKeyDown: { type: Boolean, default: false },
  maxWidthClass: { type: String, default: 'max-w-[640px]' },
  fullScreen: { type: Boolean, default: false },
  fullScreenMaxWidthClass: { type: String, default: 'sm:max-w-[640px]' },
  fullScreenLayoutClass: {
    type: String,
    default:
      'h-[100dvh] max-h-none rounded-none sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-lais-xl',
  },
  headerClass: {
    type: String,
    default:
      'flex shrink-0 items-start justify-between gap-3 border-b border-lais-border px-4 py-4 sm:items-center sm:px-6',
  },
  contentClass: {
    type: String,
    default:
      'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5',
  },
});

const emit = defineEmits(['close']);

const closeLabel = 'Fechar';
const overlayStyle = { backgroundColor: 'rgba(15, 23, 42, 0.42)' };
const titleId = `lais-modal-${Math.random().toString(36).slice(2)}`;
const labelledBy = computed(() => (props.title ? titleId : undefined));
const panelClass = computed(() => [
  'flex w-full min-w-0 flex-col overflow-hidden border border-lais-border bg-white shadow-lais-pop',
  props.fullScreen
    ? props.fullScreenLayoutClass
    : 'max-h-[calc(100dvh-2rem)] rounded-lais-xl',
  props.fullScreen ? props.fullScreenMaxWidthClass : props.maxWidthClass,
]);
const { dialogRef } = useDialogA11y({
  isOpen: toRef(props, 'show'),
  closeOnEscape: computed(() => !props.disableEscapeKeyDown),
  onClose: () => emit('close'),
});
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-lais-modal grid place-items-center overflow-y-auto overscroll-contain p-3 py-[calc(0.75rem+env(safe-area-inset-top))] pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:p-4"
      :style="overlayStyle"
      @click.self="emit('close')"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="labelledBy"
        tabindex="-1"
        :class="panelClass"
      >
        <header v-if="title || $slots.header" :class="headerClass">
          <slot name="header">
            <h2
              :id="titleId"
              class="min-w-0 break-words font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
            >
              {{ title }}
            </h2>
          </slot>
          <button
            type="button"
            class="grid size-9 place-items-center rounded-full text-lais-text-muted hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus"
            :aria-label="closeLabel"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </header>
        <div :class="contentClass">
          <slot />
        </div>
        <footer
          v-if="$slots.footer"
          class="max-h-[40dvh] shrink-0 overflow-y-auto overscroll-contain border-t border-lais-border px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:max-h-none sm:overflow-visible sm:px-6"
        >
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </div>
</template>
